<!--
  Top.vue - メイン地図表示画面
  このコンポーネントは地図の表示と操作を担当します。
  主な機能：
  - ZIPS Map APIを使用した地図の表示
  - 地図のズーム・移動・回転操作
  - 地図スタイルの切り替え
  - DCN（レイヤー）情報の表示
  - 地図データの保存・読み込み
  - チュートリアルの表示
-->
<template>
  <v-main>
    <Help />
    <Tutorial ref="tutorial" />

    <!-- モーダルウィンドウ - 地図データ保存完了時の表示 -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <img src="@/assets/modal-close.png" alt="閉じる" class="close-button" @click="closeModal" />
        <div class="modal-body">
          <p class="modal-title">編集データを保存しました</p>
          <p class="modal-note">再編集する場合はこちらから。</p>
          <a :href="mapediturl" target="_blank" class="modal-link">{{ mapediturl }}</a>
          <img src="@/assets/url-copy.png" alt="共有用URLコピー" class="modal-link-img" @click="copyUrl($event, mapediturl)" />
          <p class="modal-note">これらのURLは作成から30日間保持されます。</p>
        </div>
      </div>
    </div>

    <!-- DCN情報表示モーダル - 地図クリック時のレイヤー情報 -->
    <div v-if="showDCN" class="modal-overlay" @click="closeModal">
      <div class="modal-content-dcn" @click.stop>
        <img src="@/assets/modal-close.png" alt="閉じる" class="close-button" @click="closeModal" />
        <div class="modal-body">
          <table class="layer-table">
            <thead>
              <tr>
                <th>レイヤー論理名</th>
                <th>レイヤー物理名</th>
                <th>形状種別</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in currentDCN" :key="index">
                <td>{{ row['name'] }}</td>
                <td>{{ row['dcn'] }}</td>
                <td>{{ row['type'] }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- メインコンテナ - 地図とサイドバーの配置 -->
    <div class='main-container'>
      <!-- ZIPS Map表示エリア -->
      <!-- <div id='ZMap' :class="{ hidden: !isMapVisible }" ref="screenshotTarget"></div> -->
      <div id='ZMap'></div>
      <!-- ローディング表示 -->
      <Loading :isVisible="isLoading" :fullScreen="true" />
      <!-- 左サイドバー - チャット画面とヘッダー -->
      <div class='left-container' :class="{ closed: !isOpen }">
        <!-- ヘッダー - タイトル画面への遷移 -->
        <div class="header" @click="navigateToTitle">
          <img class="header-chat" src="@/assets/title/logo_designmapai.png" />
        </div>
        <!-- チャット画面 - 地図作成タスク完了後に表示 -->
        <ChatScreen v-if="isTaskComplete" :mapid="mapid" :idFlg="idFlg" :mapshareurl="mapshareurl" :zoomlevel="currentZoomLevel" v-model="dcnText" />
        <!-- <ChatScreen v-if="isTaskComplete" :mapid="mapid" :idFlg="idFlg" :mapshareurl="mapshareurl" :zoomlevel="currentZoomLevel" v-model="inputComment" /> -->
      </div>

      <!-- 右側操作エリア -->
      <div class='right-container'>
        <!-- <div class="position-back-img">
          <img src="@/assets/btn_back.png" alt="HeaderImage" @click="resetPosition" />
        </div> -->

        <div class="image-container">
          <img src="@/assets/zoom.png" @click="handleClick" ref="image" class="clickable-image" />
        </div>

        <!-- 地図回転ボタン -->
        <div class="rotation-buttons">
          <img
            src="@/assets/heading_right.png"
            alt="右回転"
            class="rotation-button"
            @click="rotateMapRight"
          />
          <img
            src="@/assets/heading_reset.png"
            alt="リセット"
            class="rotation-button"
            @click="resetMapRotation"
          />
          <img
            src="@/assets/heading_left.png"
            alt="左回転"
            class="rotation-button"
            @click="rotateMapLeft"
          />
        </div>

        <!-- ズーム操作ボタン -->
        <div class="image-container">
          <img src="@/assets/zoom.png" @click="handleClick" ref="image" class="clickable-image" />
        </div>

        <!-- 利用規約・外部送信ポリシーリンク -->
        <div class="copyright">
          <span>
            <a :href="`https://${hostname}/design_map_ai/termsOfUse`" target="_blank" rel="noopener noreferrer">
              利用規約
            </a>
          </span>
          <span>
            <a :href="`https://${hostname}/design_map_ai/externalTransmissionPolicy`" target="_blank" rel="noopener noreferrer">
              外部送信ポリシー
            </a>
          </span>
        </div>

        <div v-if="showKiyaku" class="modal-overlay" @click="closeKiyaku">
          <div class="modal-content" @click.stop>
            <img src="@/assets/modal-close.png" alt="閉じる" class="close-button" @click="closeKiyaku" />
            <div class="modal-body">
              <p class="modal-title">規約</p>
              <p class="modal-note">規約内容をここに記載</p>
              <p class="modal-title">免責</p>
              <p class="modal-note">免責内容をここに記載</p>
              <p class="modal-title">問い合わせ</p>
              <p class="modal-note">問い合わせ内容をここに記載</p>
            </div>
          </div>
        </div>

        <!-- クリップボードにコピー -->
        <div v-if="showClip" class="temporary-text" :style="{ top: `${position.y}px`, left: `${position.x}px` }">
          {{ clipmessage }}
        </div>

      </div>
    </div>

  </v-main>
</template>

<script>
import config from '@/config'
import ChatScreen from '@/components/ChatScreen.vue'
import Map from './zips-map.js'
import axios from 'axios'
import { EventBus } from '@/views/eventBus.js'
import Loading from '@/components/Loading.vue'
import Help from '@/components/Help.vue'
import fcc from './fcc.json'
import fccArea from './output_area.json'
import gmapDesign from './design_gmap.json'
import whitemapDesign from './design_white.json'
import whiteKidsmapDesign from './design_white_kids.json'
import gmapKidsDesign from './design_gmap_kids.json'
import fccLine from './output_line.json'
import fccNote from './output_note.json'
import Tutorial from '@/components/Tutorial.vue'

export default {
  name: 'Top',
  // 使用するコンポーネント
  components: {
    ChatScreen, // チャット画面
    Loading, // ローディング表示
    Help, // ヘルプ画面
    Tutorial // チュートリアル
  },

  data () {
    return {
      /**
       * 各コンポーネントに渡す値
       * ※最初に初期化して呼び出すときは this.hogeModalItems.item = hoge のように指定する
       * ※this.hogeModalItems自体を上書きしないこと（表示中の要素とは別物扱いとなる）
       */
      // モーダル表示制御
      showModal: false, // モーダルの表示状態を管理
      mapshareurl: '', // 地図共有用URL
      mapediturl: '', // 地図編集用URL
      showDCN: false, // DCN情報表示モーダルの表示状態

      // 別名で保存ダイアログ
      saveAsModalItems: {
        isActive: false,
        mapName: null,
        clickPositive: this.clickSaveAsMapDesign
      },

      // メッセージダイアログ
      messageModalItems: {
        isActive: false,
        message: null,
        clickButton: null
      },

      // YesNoダイアログ
      yesNoModalItems: {
        isActive: false,
        message: null,
        clickPositive: null,
        clickNegative: null
      },

      /**
       * 親要素で使う値
       */
      // エラー・状態管理
      isError: false, // エラー状態
      isEditting: false, // 編集中フラグ
      isUnsaved: false, // 未保存フラグ
      errorMessage: null, // エラーメッセージ

      // 地図関連
      map: null, // ZIPS Mapインスタンス
      centerLat: null, // 地図中心緯度
      centerLng: null, // 地図中心経度
      currentZoomLevel: 1, // 現在のズームレベル
      zoomLevels: config.mapZoomLevels, // 利用可能なズームレベル

      // 地図データ
      inputFCC: '', // 入力FCC
      getGroupResult: null, // グループ取得結果
      mapDesign: {}, // 地図デザイン設定

      // 基本設定
      center: '35.6813821,139.7660968', // デフォルト中心座標
      mapid: String(Date.now()) + '-' + Math.random().toString(36).substring(2, 8), // 地図ID

      // UI表示制御
      showClip: false, // クリップボードコピー表示フラグ
      showMessage: false, // メッセージ表示フラグ
      position: { x: 0, y: 0 }, // 表示位置
      clipmessage: 'クリップボードにコピーしました', // クリップボードメッセージ
      centermessage: '', // 中央メッセージ

      // 地図スタイル・設定
      mapType: 'gmap', // 地図タイプ（gmap, white）
      mapStyle: null, // 地図スタイル設定
      zoom: 16, // 初期ズームレベル
      lat: 35.6813821, // 初期緯度
      lng: 139.7660968, // 初期経度

      // 初期値保存
      initLat: null, // 初期緯度
      initLng: null, // 初期経度
      initZoomLevel: null, // 初期ズームレベル

      // フラグ・状態
      idFlg: false, // mapid指定の場合はtrue
      isOpen: true, // サイドバー開閉状態
      isLoading: true, // ローディング状態
      isTaskComplete: false, // タスク完了フラグ
      isKiyaku: false, // 規約表示フラグ

      // 地図データ・スタイル
      retStyle: null, // 戻りスタイル
      tmpmapDesign: {}, // 一時地図デザイン
      selectedMapIndex: null, // 選択された地図インデックス
      selectMapDesign: null, // 選択された地図デザイン

      // 設定・フォント
      Font: '', // フォント設定
      startLat: null, // 開始緯度
      startLng: null, // 開始経度
      startZoom: null, // 開始ズームレベル

      // システム設定
      hostname: config.domain, // ホスト名
      shorturl: config.shorturl, // 短縮URL

      // 表示制御
      isMapVisible: false, // 地図表示フラグ

      // DCN情報
      currentDCN: null, // 現在のDCN情報
      dcnText: '', // DCNテキスト
      clickLatlng: null, // クリック座標

      // 地図回転
      currentRotation: 0, // 現在の回転角を保存

      // kidsイベント用の座標
      kidsLat: 35.80659461393563,
      kidsLng: 139.78884822039177,

      // モード・状態
      mapMode: null, // 地図モード
      isFirstTime: true, // 初回フラグ（ズームレベル設定用）

      // 規約表示
      showKiyaku: false // 規約表示フラグ
    }
  },

  async mounted () {
    // 地図IDが指定されている場合、既存地図を読み込み
    if (this.$route.query.mapid !== '' && this.$route.query.mapid !== null && this.$route.query.mapid !== undefined) {
      await this.getMap()
      this.idFlg = true
    }

    // kidsイベントの場合の特別な設定
    if (this.$route.query.event === 'kids') {
      this.center = `${this.kidsLat},${this.kidsLng}`
      this.lat = this.kidsLat
      this.lng = this.kidsLng
      this.mapMode = 'kids'
      if (this.idFlg === false) {
        // mapid指定なし
        this.mapType = 'gmap_kids'
      }
    }

    // 地図を描画
    await this.drawMap()

    // 地図タイプに応じてスタイルを設定
    if (this.mapType === 'white_kids') {
      this.map.setWhiteKidsMap()
    } else if (this.mapType === 'gmap_kids') {
      this.map.setGoogleMapKids()
    } else if (this.mapType === 'white') {
      this.map.setWhiteMap()
    } else if (this.mapType === 'gmap') {
      this.map.setGoogleMap()
    }

    // フォント設定がある場合は適用
    if (this.Font !== '') {
      this.map.changeFont(this.Font)
    }

    // 既存地図の場合、スタイルを復元
    if (this.idFlg === true) {
      if (this.isJson(this.mapStyle)) {
        this.map.updateStyleMap(this.mapStyle)
        this.mapDesign = this.mapStyle
      }
    }

    // EventBusで地図操作イベントを監視
    EventBus.$on('changeMapDesign', (event) => {
      // 地図スタイル選択時の処理
      if (event === 0 || event === 1) {
        this.handleMapStyleSelect(event)
      } else if (event === 'back') {
        // 戻る処理
        this.isLoading = true
        this.map.updateStyleMap(this.retStyle)
        this.isLoading = false
      } else if (event === 'ok') {
        // OK処理
        if (Object.keys(this.mapDesign).length === 0) {
          // 初回
          this.mapDesign = this.tmpmapDesign
        } else {
          this.mapDesign = this.mergeJson(this.mapDesign, this.tmpmapDesign)
        }
      } else if (event === 'shareMap') {
        // 地図共有
        this.saveMap(false)
      } else if (event === 'saveMap') {
        // 地図保存
        this.saveMap(true)
      } else {
        // AIデザイン処理
        if (event.Designs) {
          this.retStyle = this.map.updateStyleMap(event.Designs)
          this.tmpmapDesign = event.Designs
        }
      }
      // this.mapDesign = JSON.parse(event)
    })

    // dcnTextを初期化するイベントリスナー
    EventBus.$on('resetDcnText', () => {
      this.dcnText = ''
    })

    // 初期化完了
    this.isLoading = false
    this.isTaskComplete = true

    // 初回のみチュートリアルを表示
    if (!this.$route.query.mapid) {
      const hasSeenTutorial = localStorage.getItem('hasSeenTutorial')
      if (!hasSeenTutorial) {
        this.$refs.tutorial.openTutorial()
        localStorage.setItem('hasSeenTutorial', 'true')
      }
    }
  },

  destroyed () {
    // マップインスタンスを解放、イベントを削除
    if (!this.map) {
      return
    }
    this.map.destroyed()
    delete this.map
    this.map = null
    this.$unloadScript('https://' + config.zipsApi.domain + '/zma_loader.js?key=' + config.zipsApi.key + '&auth=' + config.zipsApi.authType)
  },

  methods: {
    /**
     * 地図を描画する
     * ZIPS Map APIを使用して地図インスタンスを作成し、初期化します
     */
    async drawMap () {
      // ZIPS Map APIスクリプトを読み込み
      await this.$loadScript('https://' + config.zipsApi.domain + '/zma_loader.js?key=' + config.zipsApi.key + '&auth=' + config.zipsApi.authType)
      // Mapクラスのインスタンスを作成
      this.map = new Map('ZMap', 'kP8KjZdn', this.zoom, this.lat, this.lng, true, true)
      // Mapクラスの中心位置をこちらの値に反映するリスナーを登録
      this.map.setMapCenterListener((centerLat, centerLng) => {
        this.centerLat = centerLat
        this.centerLng = centerLng
      })
      // ズームレベル変更リスナーを登録
      this.map.setMapZoomListener((zoomLevel) => {
        this.currentZoomLevel = zoomLevel
      })
      // DCN(レイヤ)情報を表示するリスナーを登録
      this.map.setMapClickLatLngListener((dcn, latlng) => {
        this.currentDCN = dcn
        this.clickLatlng = latlng
        this.createDCNWidget()
      })
      // 地図を初期化
      await this.map.initMap()
      // コンパスを追加
      this.map.setCompass()

      // 地図の初期化完了後、チュートリアル表示の準備
      // this.$nextTick(() => {
      //   setTimeout(() => {
      //     this.isMapVisible = true
      //     // /top遷移時にチュートリアルを表示（地図初期化完了後）
      //     if (this.$route.path === '/top' && !this.$route.query.mapid) {
      //       setTimeout(() => {
      //         this.$refs.tutorial.openTutorial()
      //       }, 200)
      //     }
      //   }, 300)
      // })
    },

    /**
     * DCNウィジェットを作成する
     * 地図クリック時に表示されるレイヤー情報のウィジェットを作成します
     */
    createDCNWidget () {
      // DCN名を設定
      this.setDCNName()

      // 地図タイプに応じて適切なJSONデータを選択
      let mapJson
      if (this.mapType === 'gmap') {
        mapJson = gmapDesign
      } else if (this.mapType === 'gmap_kids') {
        mapJson = gmapKidsDesign
      } else if (this.mapType === 'white_kids') {
        mapJson = whiteKidsmapDesign
      } else {
        mapJson = whitemapDesign
      }

      // DCNをタイプ順（note, line, poly）でソート
      const sortedDCN = [...this.currentDCN].sort((a, b) => {
        const typeOrder = { note: 0, line: 1, poly: 2 }
        return typeOrder[a.type] - typeOrder[b.type]
      })

      // DCN情報表示用のHTMLテーブルを作成
      const dcnHeaders = ['レイヤー論理名', 'レイヤー物理名', '形状', 'ズームレベル', 'デザイン']
      const dcnHTML = `
        <img id="dcn_close" src="img/modal-close.png" style="height: 20px; padding-left: 96%; cursor: pointer;" />
                <span>現在のズームレベル：${this.currentZoomLevel}</span>
        <table border="1" style="border-collapse: collapse;">
          <tr>${dcnHeaders.map(h => `<th style="padding: 5px;">${h}</th>`).join('')}</tr>
          ${sortedDCN.map((dcn, index) => `
            <tr>
              <td style="padding: 5px;">${dcn.name}</td>
              <td style="padding: 5px;">${dcn.dcn}<img src="img/url-copy.png" id="${dcn.dcn}_${dcn.type}" style="padding-left: 5px; cursor: pointer;" /></td>
              <td style="padding: 5px;">${dcn.type === 'poly' ? '面' : dcn.type === 'line' ? '線' : '文字'}</td>
              <td style="padding: 5px;">${this.convertZoomLevelsToRange(dcn.zoomlevel)}</td>
              <td style="padding: 5px;"><canvas id="${dcn.dcn}_canvas_${dcn.type}" width="100" height="50"></canvas></td>
            </tr>
          `).join('')}
        </table>
      `
      // ウィジェットを地図に追加
      this.map.addDCNWidget(dcnHTML, this.clickLatlng)

      // 閉じるボタンのイベントリスナーを設定
      document.getElementById('dcn_close').addEventListener('click', () => {
        this.map.removeAllWidgets()
        this.map.resetClickDCN()
      })

      // 各DCN要素のイベントリスナーとキャンバス描画を設定
      this.currentDCN.forEach((dcn, index) => {
        // DCN名コピーボタンのイベントリスナー
        const el = document.getElementById(`${dcn.dcn}_${dcn.type}`)
        if (el) {
          el.addEventListener('click', () => {
            this.dcnText = this.dcnText + dcn.dcn + `(${dcn.type})`
          })
        }

        // キャンバス要素の取得と描画処理
        const canvas = document.getElementById(`${dcn.dcn}_canvas_${dcn.type}`)
        if (canvas) {
          if (dcn.type === 'poly') {
            // console.log(this.retStyle)
            // console.log(this.mapDesign)
            let currentMatched = null
            if (Object.keys(this.mapDesign).length > 0 && this.mapDesign.poly.length > 0) {
              // console.log(this.mapDesign.poly)
              // console.log(this.mapDesign.poly[0])
              // console.log(this.mapDesign.poly[2])
              currentMatched = this.mapDesign.poly.find(item => item[0] === 'DCN:' + dcn.dcn.split('_')[0])
              // const gmap_matched = gmapDesign.find(item => item.fcc === dcn.dcn && item.scale === Math.floor(this.currentZoomLevel))

              // const getColorById = (data, targetId) => {
              //   const match = data.poly.find(item => item[0] === targetId)
              //   return match?.[1]?.color || null
              // }
              // // 使用例
              // currentMatched = getColorById(this.mapDesign, 'DCN:' + dcn.dcn)
              // console.log(currentMatched)
              if (currentMatched) {
                // console.log('current_matched!!')
                const ctx = canvas.getContext('2d')
                const col = currentMatched[1].color
                // console.log(col)
                ctx.fillStyle = `rgb(${col[0]}, ${col[1]}, ${col[2]})`
                // 四角形を中央に配置（canvas: 100x50, 四角形: 40x40）
                ctx.fillRect(30, 5, 40, 40)
                // 白なら黒枠
                if (col[0] === 255 && col[1] === 255 && col[2] === 255) {
                  ctx.strokeStyle = 'rgb(0,0,0)'
                  ctx.lineWidth = 1
                  // ctx.strokeRect(30, 5, 40, 40)
                  ctx.strokeRect(30.5, 5.5, 40, 40) // 0.5ずらしてシャープな線に
                }
              }
            }
            if (!currentMatched) {
              const mapMatched = mapJson.find(item => item.fcc === dcn.dcn && item.type === 'poly' && item.scale.includes(Math.floor(this.currentZoomLevel)))
              // const gmap_matched = gmapDesign.find(item => item.fcc === dcn.dcn && item.scale === Math.floor(this.currentZoomLevel))
              if (mapMatched && mapMatched.def.color !== undefined) {
                // console.log('map_matched!!')
                const ctx = canvas.getContext('2d')
                const col = mapMatched.def.color
                // console.log(col)
                ctx.fillStyle = `rgb(${col[0]}, ${col[1]}, ${col[2]})`
                // 四角形を中央に配置（canvas: 100x50, 四角形: 40x40）
                ctx.fillRect(30, 5, 40, 40)
                // 白なら黒枠
                if (col[0] === 255 && col[1] === 255 && col[2] === 255) {
                  ctx.strokeStyle = 'rgb(0,0,0)'
                  ctx.lineWidth = 1
                  ctx.strokeRect(30, 5, 40, 40)
                }
              } else {
                // const matched = fccArea.find(item => item.design_cat_name === dcn.dcn)
                const matched = fccArea.find(item => item.design_cat_name === dcn.dcn && item.scl === String(Math.floor(this.currentZoomLevel)))
                if (matched) {
                  // console.log('matched!!')
                  // console.log(matched.pyc)
                  const ctx = canvas.getContext('2d')
                  const col = this.RGB2Json(matched.pyc)
                  // console.log(col)
                  ctx.fillStyle = `rgb(${col[0]}, ${col[1]}, ${col[2]})`
                  // 四角形を塗りつぶす
                  ctx.fillRect(30, 5, 40, 40)
                  // 白なら黒枠
                  if (col[0] === 255 && col[1] === 255 && col[2] === 255) {
                    ctx.strokeStyle = 'rgb(0,0,0)'
                    ctx.lineWidth = 1
                    ctx.strokeRect(30, 5, 40, 40)
                  }
                }
              }
            }
          } else if (dcn.type === 'line') {
            // console.log('line処理開始:', dcn.dcn)
            const ctx = canvas.getContext('2d')
            if (!ctx) {
              console.error('canvas context取得失敗:', dcn.dcn)
              return
            }
            // canvasの背景を白で塗りつぶし
            ctx.fillStyle = 'rgb(255, 255, 255)'
            ctx.fillRect(0, 0, canvas.width, canvas.height)

            // まず現在のマップデザインから検索
            let currentMatchedList = []
            if (Object.keys(this.mapDesign).length > 0 && this.mapDesign.line && this.mapDesign.line.length > 0) {
              currentMatchedList = this.mapDesign.line.filter(item => {
                const isMatch = item[0] === 'DCN:' + dcn.dcn.split('_')[0]
                if (item[2] && Array.isArray(item[2])) {
                  return isMatch && item[2].includes(Math.floor(this.currentZoomLevel))
                } else {
                  return isMatch
                }
              })
              // console.log('currentMatchedList検索結果:', currentMatchedList)
            }

            if (currentMatchedList.length > 0) {
              // console.log('current_matched複数件!!')
              currentMatchedList.forEach((currentMatched, index) => {
                // console.log(`currentMatched[${index}]処理開始`)
                const col = currentMatched[1].color1 || currentMatched[1].color
                const lineWidth = currentMatched[1].size1 || 2
                // 白なら背景色を追加
                if (col[0] === 255 && col[1] === 255 && col[2] === 255) {
                  ctx.fillStyle = 'rgb(229, 227, 223)'
                  ctx.fillRect(10, 15, 80, 20)
                }
                ctx.strokeStyle = `rgb(${col[0]}, ${col[1]}, ${col[2]})`
                ctx.lineWidth = lineWidth
                ctx.beginPath()
                ctx.moveTo(10, 25)
                ctx.lineTo(90, 25)
                ctx.stroke()
                // console.log(`currentMatched[${index}]で描画完了`)
              })
            } else {
              // マップJSONから検索
              const mapMatchedList = mapJson.filter(item => item.fcc === dcn.dcn && item.type === 'line' && item.scale.includes(Math.floor(this.currentZoomLevel)))
              // console.log('mapMatchedList検索結果:', mapMatchedList)
              if (mapMatchedList.length > 0) {
                // console.log('map_matched複数件!!')
                mapMatchedList.forEach((mapMatched, index) => {
                  if (mapMatched.def.color1 !== undefined) {
                    // console.log(`mapMatched[${index}]処理開始`)
                    const col = mapMatched.def.color1
                    const lineWidth = mapMatched.def.size1 || 2
                    // 白なら背景色追加
                    if (col[0] === 255 && col[1] === 255 && col[2] === 255) {
                      ctx.fillStyle = 'rgb(229, 227, 223)'
                      ctx.fillRect(10, 15, 80, 20)
                    }
                    ctx.strokeStyle = `rgb(${col[0]}, ${col[1]}, ${col[2]})`
                    ctx.lineWidth = lineWidth
                    ctx.beginPath()
                    ctx.moveTo(10, 25)
                    ctx.lineTo(90, 25)
                    ctx.stroke()
                    // console.log(`mapMatched[${index}]で描画完了`)
                  }
                })
              } else {
                // fccLineから検索
                const matchedList = fccLine.filter(item => item.design_cat_name === dcn.dcn && item.scl === String(Math.floor(this.currentZoomLevel)))
                // console.log('fccLine matchedList検索結果:', matchedList)
                if (matchedList.length > 0) {
                  // console.log('fccLine_matched複数件!!')
                  matchedList.forEach((matched, index) => {
                    if (matched.lnc) {
                      // console.log(`fccLine matched[${index}]処理開始`)
                      const col = this.RGB2Json(matched.lnc)
                      const lineWidth = parseInt(matched.lnw) || 2
                      const linePattern = matched.lnp ? matched.lnp.split('|').map((s) => (parseInt(s))) : null
                      // linePatternが1の場合は実線（空の配列）にする
                      const finalLinePattern = linePattern && linePattern.length === 1 && linePattern[0] === 1 ? [] : linePattern
                      // 白なら背景色追加
                      if (col[0] === 255 && col[1] === 255 && col[2] === 255 && Array.isArray(finalLinePattern) && finalLinePattern.length === 0) {
                        ctx.fillStyle = 'rgb(229, 227, 223)'
                        ctx.fillRect(10, 15, 80, 20)
                      }
                      ctx.strokeStyle = `rgb(${col[0]}, ${col[1]}, ${col[2]})`
                      ctx.lineWidth = lineWidth
                      ctx.beginPath()
                      ctx.moveTo(10, 25)
                      ctx.lineTo(90, 25)
                      if (matched.lnp) {
                        ctx.setLineDash(finalLinePattern)
                      }
                      ctx.stroke()
                      // console.log(`fccLine matched[${index}]で描画完了`)
                    }
                  })
                }
              }
            }
            // console.log('line処理完了:', dcn.dcn)
          } else if (dcn.type === 'note') {
            const ctx = canvas.getContext('2d')
            let currentMatched = null
            if (Object.keys(this.mapDesign).length > 0 && this.mapDesign.note && this.mapDesign.note.length > 0) {
              currentMatched = this.mapDesign.note.find(
                item => item[0] === 'DCN:' + dcn.dcn.split('_')[0] && Array.isArray(item[2]) && item[2].includes(Math.floor(this.currentZoomLevel))
              )
            }
            if (currentMatched) {
              // mapDesignのnoteで描画
              const col = currentMatched[1].TXcolor || [0, 0, 0, 255]
              const col2 = currentMatched[1].TBcolor || [0, 0, 0, 255]
              ctx.fillStyle = `rgb(${col[0]}, ${col[1]}, ${col[2]})`
              ctx.strokeStyle = `rgb(${col2[0]}, ${col2[1]}, ${col2[2]})`
              // ctx.fillRect(30, 5, 40, 40)
              ctx.font = '16px sans-serif'
              // ctx.fillStyle = `rgb(${col[0]}, ${col[1]}, ${col[2]})`
              ctx.textAlign = 'center'
              ctx.textBaseline = 'middle'
              ctx.fillText('サンプル', canvas.width / 2, canvas.height / 2)
            } else {
              // fccNoteから検索（現状の処理）
              const matched = fccNote.find(item => item.design_cat_name === dcn.dcn && item.scl === String(Math.floor(this.currentZoomLevel)))
              if (matched) {
                // console.log(matched)
                // console.log('matched!!')
                if (matched.pyc !== '') {
                  const colp = this.RGB2Json(matched.pyc)
                  // console.log(col)
                  if (colp[0] === 0 && colp[1] === 0 && colp[2] === 0) {
                    ctx.fillStyle = 'rgb(255, 255, 255)'
                  } else {
                    ctx.fillStyle = `rgb(${colp[0]}, ${colp[1]}, ${colp[2]})`
                  }
                  // 四角形を中央に配置（canvas: 100x50, 四角形: 40x40）
                  ctx.fillRect(30, 5, 40, 40)
                }
                ctx.font = matched.fnt
                const col = this.RGB2Json(matched.fnc)
                const col2 = this.RGB2Json(matched.bdc)
                // console.log(col)
                ctx.fillStyle = 'rgb(' + col[0] + ',' + col[1] + ',' + col[2] + ' )'
                ctx.strokeStyle = 'rgb(' + col2[0] + ',' + col2[1] + ',' + col2[2] + ' )'
                ctx.textAlign = 'center'
                ctx.textBaseline = 'middle'
                ctx.fillText('サンプル', canvas.width / 2, canvas.height / 2)
              }
            }
          }
        }
      })
    },
    /**
     * RGB文字列をJSON配列に変換する
     * @param {string} RGB - "R|G|B|A"形式のRGB文字列
     * @returns {Array} RGB値の配列 [R, G, B, A]
     */
    RGB2Json (RGB) {
      const ret = [0, 0, 0, 0]
      if (RGB) {
        const RGBArray = RGB.split('|')
        if (RGBArray.length >= 3) {
          ret[0] = parseInt(RGBArray[0], 10)
          ret[1] = parseInt(RGBArray[1], 10)
          ret[2] = parseInt(RGBArray[2], 10)
          ret[3] = RGBArray[3] ? parseInt(RGBArray[3], 10) : 0
        }
      }
      return ret
    },

    /**
     * タイトル画面に遷移する
     */
    navigateToTitle () {
      this.$router.push({ name: 'Title' })
    },
    // resetPosition () {
    //   if (this.startLat !== null && this.startLng !== null && this.startZoom !== null) {
    //     this.map.moveTo(this.startLat, this.startLng)
    //     this.map.zoomMap(this.startZoom)
    //   } else if (this.initLat !== null && this.initLng !== null && this.initZoomLevel !== null) {
    //     this.map.moveTo(this.initLat, this.initLng)
    //     this.map.zoomMap(this.initZoomLevel)
    //   }
    // },
    /**
     * ズームボタンのクリックイベントを処理する
     * ボタンの上半分クリックでズームイン、下半分クリックでズームアウト
     * @param {Event} event - クリックイベント
     */
    handleClick (event) {
      const image = this.$refs.image
      const rect = image.getBoundingClientRect()
      const clickY = event.clientY - rect.top // 画像内でのY座標
      const halfHeight = rect.height / 2

      if (clickY < halfHeight) {
        this.handleTopClick()
      } else {
        this.handleBottomClick()
      }
    },
    /**
     * ズームイン処理
     */
    handleTopClick () {
      this.currentZoomLevel += 0.5
      this.map.zoomMap(this.currentZoomLevel)
    },
    /**
     * ズームアウト処理
     */
    handleBottomClick () {
      this.currentZoomLevel -= 0.5
      this.map.zoomMap(this.currentZoomLevel)
    },
    /**
     * マップスタイル選択時の処理
     * 地図の種類（Google Map、白地図、路線図等）を切り替えます
     * @param {number} index - 選択されたスタイルのインデックス
     */
    handleMapStyleSelect (index) {
      if (this.selectedMapIndex === index) return

      this.selectedMapIndex = index
      // デフォルト地図に戻す
      if (this.selectMapDesign !== null) {
        if (this.mapMode === null) {
          this.map.updateStyleMap(this.selectMapDesign)
          this.selectMapDesign = this.map.setGoogleMap()
          this.mapType = 'gmap'
        } else {
          this.map.updateStyleMap(this.selectMapDesign)
          this.selectMapDesign = this.map.setGoogleMapKids()
          this.mapType = 'gmap_kids'
        }
      }
      // 選択地図に更新
      if (index === 1) {
        if (this.mapMode === null) {
          this.selectMapDesign = this.map.setWhiteMap()
          this.mapType = 'white'
          if (this.isFirstTime) {
            this.map.setZoom(11)
            this.isFirstTime = false
          }
        } else {
          this.selectMapDesign = this.map.setWhiteKidsMap()
          this.mapType = 'white_kids'
        }
      }
    },
    /**
     * 既存地図データを取得する
     * URLパラメータのmapidを使用して地図データをAPIから取得します
     */
    async getMap () {
      const response = await axios.get('/design_map_ai/api/getmap/' + this.$route.query.mapid)

      if (response.data.status.returnCode !== 0 || (Array.isArray(response.data.result.map) && response.data.result.map.length === 0)) {
        this.$router.push('NotFound')
        return
      }
      const res = response.data.result.map

      this.mapType = res.mapType
      this.mapStyle = res.mapStyle
      this.zoom = res.zoomLevel
      this.lat = res.centerLat
      this.lng = res.centerLng
      this.initLat = res.initLat
      this.initLng = res.initLng
      this.initZoomLevel = res.initZoomLevel
      this.mapid = this.$route.query.mapid
    },

    /**
     * 規約モーダルを閉じる
     */
    closeKiyaku () {
      this.showKiyaku = false
    },

    /**
     * 規約モーダルを開く
     */
    openKiyaku () {
      this.showKiyaku = true
    },

    /**
     * DCN名を設定する
     * マスタデータに基づいて論理名とズームレベルを設定します
     */
    setDCNName () {
      // マスタに基づいて論理名を置換
      // マスタに一致する論理名を「マスタ論理名」として追加
      this.currentDCN = this.currentDCN.map(row => {
        const match = fcc.find(m => m.fcc === row.dcn)
        return {
          ...row,
          name: match ? match.fccName : null,
          zoomlevel: match ? match.scale : null
        }
      })
    },

    /**
     * 2つのJSONオブジェクトをマージする
     * @param {Object} json1 - 1つ目のJSONオブジェクト
     * @param {Object} json2 - 2つ目のJSONオブジェクト
     * @returns {Object} マージされたJSONオブジェクト
     */
    mergeJson (json1, json2) {
      const merged = {}

      // Collect keys from both JSONs
      const allKeys = new Set([...Object.keys(json1), ...Object.keys(json2)])

      allKeys.forEach((key) => {
        const value1 = json1[key] || []
        const value2 = json2[key] || []

        // Merge arrays for the same key
        if (Array.isArray(value1) && Array.isArray(value2)) {
          merged[key] = [...value1, ...value2]
        } else {
          // Handle non-array cases (last value wins)
          merged[key] = value2 || value1
        }
      })

      return merged
    },

    /**
     * 地図データを保存する
     * @param {boolean} show - 保存完了モーダルを表示するかどうか
     */
    async saveMap (show) {
      this.showModal = show
      const zoom = this.map.getZoomLevel()
      const center = this.map.getCenterPosition()
      if (this.startLat == null && this.startLng == null && this.startZoom == null) {
        // 設定なし
        this.startLat = '35.6813821'
        this.startLng = '139.7660968'
        this.startZoom = 16
      }
      const request = {
        mapId: this.mapid,
        mapType: this.mapType,
        mapStyle: this.mapDesign,
        zoomLevel: zoom,
        centerLat: Number(center.lat),
        centerLng: Number(center.lng),
        initZoomLevel: this.startZoom,
        initLat: Number(this.startLat),
        initLng: Number(this.startLng),
        chatId: this.mapid
      }

      const response = await axios.get('/design_map_ai/api/getmap/' + this.mapid)

      if (Array.isArray(response.data.result.map) && response.data.result.map.length === 0) {
        // 新規追加
        await axios.post('/design_map_ai/api/storemap', request)
          .then(() => {
          }).catch((error) => {
            console.log('エラー内容:', error)
          })
      } else {
        // update
        await axios.post('/design_map_ai/api/updatemap', request)
          .then(() => {
          }).catch((error) => {
            console.log('エラー内容:', error)
          })
      }

      if (this.mapMode === null) {
        this.mapediturl = 'https://' + this.hostname + '/design_map_ai/top?mapid=' + this.mapid
      } else {
        this.mapediturl = 'https://' + this.hostname + '/design_map_ai/top?mapid=' + this.mapid + '&event=kids'
      }
    },

    /**
     * モーダルを閉じる
     */
    closeModal () {
      this.showModal = false
      this.showDCN = false
    },

    /**
     * 値がJSON形式かどうかを判定する
     * @param {*} value - 判定対象の値
     * @returns {boolean} JSON形式の場合true
     */
    isJson (value) {
      if (typeof value === 'string') {
        // 文字列の場合、JSON.parseでチェック
        try {
          JSON.parse(value)
          return true
        } catch {
          return false
        }
      } else if (typeof value === 'object' && value !== null) {
        // オブジェクトの場合、JSONとして扱えるかチェック
        try {
          JSON.stringify(value)
          return true
        } catch {
          return false
        }
      }
      return false // その他の型はJSON形式ではない
    },

    /**
     * URLをクリップボードにコピーする
     * @param {Event} event - クリックイベント
     * @param {string} url - コピーするURL
     */
    async copyUrl (event, url) {
      try {
        // URLをクリップボードにコピー
        await navigator.clipboard.writeText(url)
      } catch (error) {
        console.error('クリップボードへのコピーに失敗しました:', error)
        this.clipmessage = 'クリップボードにコピーできませんでした'
      }

      // クリック位置を取得
      this.position = {
        x: event.clientX,
        y: event.clientY
      }
      // テキストを表示
      this.showClip = true
      // 一定時間後に非表示にする
      setTimeout(() => {
        this.showClip = false
      }, 2000) // 2秒後に非表示
    },

    convertZoomLevelsToRange (zoomLevels) {
      if (!Array.isArray(zoomLevels) || zoomLevels.length === 0) return ''
      zoomLevels.sort((a, b) => a - b)
      const ranges = []
      let start = zoomLevels[0]
      let end = start

      for (let i = 1; i < zoomLevels.length; i++) {
        if (zoomLevels[i] === end + 1) {
          end = zoomLevels[i]
        } else {
          ranges.push(start === end ? `${start}` : `${start}～${end}`)
          start = zoomLevels[i]
          end = start
        }
      }
      ranges.push(start === end ? `${start}` : `${start}～${end}`)
      return ranges.join(', ')
    },
    /**
     * 地図を右に回転する
     */
    rotateMapRight () {
      this.currentRotation -= 5
      this.map.setHeading(this.currentRotation)
    },
    /**
     * 地図を左に回転する
     */
    rotateMapLeft () {
      this.currentRotation += 5
      this.map.setHeading(this.currentRotation)
    },
    /**
     * 地図の回転をリセットする
     */
    resetMapRotation () {
      this.currentRotation = 0
      this.map.setHeading(this.currentRotation)
    }
  }
}
</script>

<style scoped lang='scss'>
/* ===== メインレイアウト ===== */

/* メインコンテナ - 地図とサイドバーの配置 */
.main-container {
  display: flex;
  height: calc(100%);
  position: relative;
}

/* 地図非表示時のスタイル */
.hidden {
  visibility: hidden;
}

/* ===== 一時的なテキスト表示 ===== */

/* クリップボードコピー完了メッセージ */
.temporary-text {
  position: absolute;           /* クリック位置に表示 */
  background-color: #3f3f3f;  /* 背景色: 黒 */
  color: #ffffff;             /* 文字色: 白 */
  padding: 7px 7px;             /* 内側の余白 */
  border-radius: 0;
  z-index: 16777273;            /* 最前面に表示 */
  white-space: nowrap;          /* テキストを折り返さない */
  pointer-events: none;         /* メッセージがクリックをブロックしない */
  font-size: 13px;
  animation: fadeIn 0.3s ease;
}

/* 中央メッセージ表示 */
.center-message {
  left: 50%;
  top: 10%;
}

/* ===== 地図表示エリア ===== */

/* ZIPS Map表示エリア */
#ZMap {
  height: 100%;
  width: calc(100% - 480px);
  min-width: 5px;
  margin-left: auto;
}

/* ===== ヘッダー ===== */

/* ヘッダー */
.header {
  height: 65px;
  width: 100%;
  max-width: 480px;
  background-color: #f2f2f2;
  box-shadow: 0 5px 5px rgba(0, 0, 0, 0.1);
  align-content: center;
  text-align: center;
}

.header img {
  width: 100%;
  max-width: 244px;
  cursor: pointer;
}

/* ===== 利用規約・ポリシー ===== */

/* 利用規約・外部送信ポリシーリンク */
.copyright {
  position: absolute;
  bottom: 0px;
  right: 10px;
  color: #fff;
  background-color: #333;
  padding: 0 5px;
  border-radius: 4px;
  font-size: 10px;
  font-family: Arial, sans-serif;
  display: flex;
  gap: 10px;
  z-index: 100;
  cursor: pointer;
}

.copyright a {
  color: #fff;
  text-decoration: none;
}

.copyright a:hover {
  text-decoration: underline;
}

/* ===== 左サイドバー ===== */

/* 左サイドバー */
.left-container {
  border-right: 1px solid #C9C9C9;
  max-width: 480px;
  width: 100%;
  background-color: #f6f6f6;
  border-right: 1px solid #ddd;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: absolute;
  transition: transform 0.3s ease;
  z-index: 2;
}

/* 左サイドバー閉じた状態 */
.left-container.closed {
  transform: translateX(-100%);
}

/* ===== 右側操作エリア ===== */

/* 右側操作エリア */
.right-container {
  z-index: 1;

  /* ズーム操作ボタン */
  .image-container {
    text-align: center;
  }

  .clickable-image {
    position: absolute;
    right: 13px;
    z-index: 100;
    bottom: 50px;
    cursor: pointer;
  }
}

/* ===== モーダル ===== */

/* モーダルオーバーレイ */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 16777272;
}

/* モーダルコンテンツ */
.modal-content {
  background: #FFFBF7;
  padding: 20px;
  text-align: center;
  position: relative;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  animation: fadeIn 0.3s ease;
  border-radius: 20px;
}

/* DCN情報表示用モーダル */
.modal-content-dcn {
  background: #FFFBF7;
  padding: 20px;
  text-align: center;
  position: relative;
  max-width: 700px;
  width: 90%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  animation: fadeIn 0.3s ease;
  border-radius: 20px;
}

/* モーダル閉じるボタン */
.close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 24px;
  height: 24px;
  cursor: pointer;
}

/* モーダルヘッダーと本文 */
.modal-body {
  margin: 20px 0;
}

.modal-title {
  font-weight: bold;
}

.modal-link {
  text-decoration: none;
  color: #007bff;
  font-size: 13px;
}

.modal-link:hover {
  text-decoration: underline;
}

.modal-link-img {
  cursor: pointer;
  padding-left: 5px;
}

.modal-note {
  font-size: 12px;
  color: #555;
  margin-top: 10px;
  margin-bottom: 5px;
}

/* ===== 地図操作ボタン ===== */

/* 地図回転ボタン群 */
.rotation-buttons {
  position: absolute;
  right: 13px;
  bottom: 100px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  z-index: 100;
}

/* 回転ボタン */
.rotation-button {
  width: 40px;
  height: 35px;
  cursor: pointer;
  transition: transform 0.2s ease;
}
</style>
